'use client'

import { constants } from '@dustin-web/microsite-config'
import { injectGatherFormScript } from '@dustin-web/microsite-baselayout/src/components/3rd-party-scripts/gather-form-script'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Animated } from '../animated'
import CloseButton from '../close-button/close-button'
import { useResponsive, useSiteContext, useTranslations } from '../hooks'
import { useCookieState } from '../hooks/use-cookie-state'
import Image from '../image'

export const BlackFridaySignup = () => {
  const { translate } = useTranslations()
  const { sourcingCompany } = useSiteContext()
  const { breakpoints } = useResponsive()
  const { cookies, setCookie } = useCookieState()

  const [ready, setReady] = useState(false)
  const [inDOM, setInDOM] = useState(false)
  const [show, setShow] = useState(false)

  const alreadyUsed = useMemo(() => !!cookies.blackFridaySignupDismissed, [])
  const delay = 18000

  // Start timer if not already used (closed or submitted)
  useEffect(() => {
    if (!alreadyUsed) {
      const readyTimeout = setTimeout(() => {
        setReady(true)
      }, delay)

      return () => {
        clearTimeout(readyTimeout)
      }
    }
  }, [])

  // Inject in DOM if the required cookies have been accepted
  useEffect(() => {
    if (ready && checkCookiesAccepted()) setInDOM(true)
  }, [ready])

  // Let the form load before finally showing the popup
  useEffect(() => {
    if (inDOM) {
      const formLoadedTimeout = setTimeout(() => {
        setShow(true)
      }, 2000)

      return () => {
        clearTimeout(formLoadedTimeout)
      }
    }
  }, [inDOM])

  const checkCookiesAccepted = () => {
    let cookiesAccepted = false

    switch (sourcingCompany) {
      case 'DK':
        cookiesAccepted = cookies.consent.marketing === 'true' ? true : false
        break
      default:
        cookiesAccepted = true
    }

    return cookiesAccepted
  }

  const getExpiryDate = () => {
    const expiryDate = new Date()
    const month = (expiryDate.getMonth() + 1) % 12
    expiryDate.setMonth(month)

    return expiryDate
  }

  const dismiss = () => {
    setCookie(constants.cookies.blackFridaySignupDismissed, true, {
      expires: getExpiryDate(),
    })
    setShow(false)
  }

  return inDOM ? (
    <aside className="c-bf-signup-container u-layer-drawer u-overflow-hidden">
      <Animated hideOnly visible={show} animationType="pull-below" onHidden={() => setInDOM(false)}>
        <div className={`c-box c-bf-signup c-bf-signup__business`}>
          <div className="row">
            <div className="col-3 c-bg-signup c-bf-signup__image">
              <Image
                className="c-bf-signup__logo"
                width={600}
                height={100}
                alt="black_week_dustin_future_figure"
                src="http://dustinweb.azureedge.net/content/aqtntsdm/dustin_colourflow_f648-005_black_friday_signup.jpg"
              />
            </div>
            <div className="c-bf-signup__form u-px-16 u-py-4">
              <h2 className="h3 u-mr-16 u-color-light-red">
                {breakpoints.max.SM
                  ? translate('Bf_Signup_Popover_Heading_Mobile')
                  : translate('Bf_Signup_Popover_Heading')}
              </h2>
              <Form />
            </div>
            <div className="col-12 col-md-9 offset-md-3 c-bf-signup-disclaimer u-px-16">
              <p>
                <small
                  dangerouslySetInnerHTML={{
                    __html: translate('Bf_Signup_Popover_Disclaimer'),
                  }}></small>
              </p>
            </div>
          </div>
          <CloseButton onClose={() => dismiss()} />
        </div>
      </Animated>
    </aside>
  ) : null
}

const Form = () => {
  const { sourcingCompany } = useSiteContext()
  const divRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current && divRef.current && !divRef.current.innerHTML) {
      const form = getInfunnelForm()

      const html = document.createRange().createContextualFragment(form)
      injectGatherFormScript()
      isFirstRender.current = false
      divRef.current.appendChild(html)
    }

    return () => {
      if (divRef.current) {
        divRef.current.innerHTML = ''
      }
    }
  }, [divRef])

  //temp fix because script adds 2 form inputs
  if (divRef?.current?.getElementsByTagName('form').item(1)) {
    try {
      divRef?.current?.getElementsByTagName('form').item(1)?.remove()
    } catch (error) {
      console.log(error)
    }
  }

  const getInfunnelForm = () => {
    let div = ''

    if (sourcingCompany === 'SE') {
      div = `<div class="c-form c-form--grid" data-infunnelapps-form-id="616d180e513f3f000dc8255e" style="display:none" data-fields="sales_group=,marketing_group=,asset=Subscribe_B2B_SE_BlackFridayPopUp2024,business_type=B2B,lead_source=Subscribe_B2B_SE_BlackFridayPopUp2024,interest_category=Subscribe_B2B_SE_BlackFridayPopUp2024,site_id=1" data-lang="sv" data-form-title="" data-redirect-url="https://www.dustin.se/bekraftelse/valkommen-black-friday" data-submit-name="Prenumerera"  ><div style="height:300px; width:100%;"><h2></h2></div></div>`
    } else if (sourcingCompany === 'DK') {
      div = `<div class="c-form c-form--grid" data-infunnelapps-form-id="616d180e513f3f000dc8255e" style="display:none" data-fields="sales_group=,marketing_group=,asset=Subscribe_B2B_DK_BlackFridayPopUp2024,business_type=B2B,lead_source=Subscribe_B2B_DK_BlackFridayPopUp2024,interest_category=Subscribe_B2B_DK_BlackFridayPopUp2024,site_id=5" data-lang="da" data-form-title="" data-redirect-url="https://www.dustin.dk/bekraeftelse/velkommen-black-friday" data-submit-name="Tilmeld"  ><div style="height:300px; width:100%;"><h2></h2></div></div>`
    } else if (sourcingCompany === 'NO') {
      div = `<div class="c-form c-form--grid" data-infunnelapps-form-id="616d180e513f3f000dc8255e" style="display:none" data-fields="sales_group=,marketing_group=,asset=Subscribe_B2B_NO_BlackFridayPopUp2024,business_type=B2B,lead_source=Subscribe_B2B_NO_BlackFridayPopUp2024,interest_category=Subscribe_B2B_NO_BlackFridayPopUp2024,site_id=3" data-lang="no" data-form-title="" data-redirect-url="https://www.dustin.no/bekreftelse/velkommen-black-friday/" data-submit-name="Abboner"  ><div style="height:300px; width:100%;"><h2></h2></div></div>`
    } else if (sourcingCompany === 'FI') {
      div = `<div class="c-form c-form--grid" data-infunnelapps-form-id="616d180e513f3f000dc8255e" style="display:none" data-fields="sales_group=,marketing_group=,asset=Subscribe_B2B_FI_BlackFridayPopUp2024,business_type=B2B,lead_source=Subscribe_B2B_FI_BlackFridayPopUp2024,interest_category=Subscribe_B2B_FI_BlackFridayPopUp2024,site_id=7" data-lang="fi" data-form-title="" data-redirect-url="https://www.dustin.fi/vahvistus/tervetuloa-black-friday/" data-submit-name="Tilaa"  ><div style="height:300px; width:100%;"><h2></h2></div></div>`
    } else if (sourcingCompany === 'NL') {
      div = `<div class="c-form c-form--grid" data-infunnelapps-form-id="616d180e513f3f000dc8255e" style="display:none" data-fields="sales_group=,marketing_group=,asset=Subscribe_B2B_NL_BlackFridayPopUp2024,business_type=B2B,lead_source=Subscribe_B2B_NL_BlackFridayPopUp2024,interest_category=Subscribe_B2B_NL_BlackFridayPopUp2024,site_id=9" data-lang="nl" data-form-title="" data-redirect-url="https://www.dustin.nl/nieuwsbrief/welkom-black-friday/" data-submit-name="Aanmelden"  ><div style="height:300px; width:100%;"><h2></h2></div></div>`
    }

    return div
  }
  return <div ref={divRef}></div>
}

export default BlackFridaySignup
